"use client";

import { Text, Box, TextField } from "@radix-ui/themes";
import React, { useState } from "react";
import {
  isValidConfirmPassword,
  isValidPassword,
} from "../services/validationService";
import {
  RegExAtLeastOneLower,
  RegExAtLeastOneNumber,
  RegExAtLeastOneSymbol,
  RegExAtLeastOneUpper,
  RegExMin8Char,
} from "../config/constants";
import PasswordViewIcon from "../common/passwordViewIcon";
import AlertMessage from "../common/alertMessage";

export type ResetProps = {
  password: string;
  email: string;
  confirmPassword: string;
  isValidPassword: boolean;
  isValidConfirmPassword: boolean;
};

type ResetPasswordProps = {
  isSubmitted: boolean;
  credentialData: ResetProps | null;
  handleCredentialData: any;
  handleBackToLogin: any;
};

const PasswordRequirements = [
  {
    label: "Minimum 8 characters",
    validation: RegExMin8Char,
  },
  {
    label: "At least one uppercase letters(A - Z)",
    validation: RegExAtLeastOneUpper,
  },
  {
    label: "At least one lowercase letters(a - z)",
    validation: RegExAtLeastOneLower,
  },
  {
    label: "At least one numbers(0 - 9)",
    validation: RegExAtLeastOneNumber,
  },
  {
    label: "At least one symbol(#, $, @, %)",
    validation: RegExAtLeastOneSymbol,
  },
];

const ResetPassword: React.FC<ResetPasswordProps> = (props) => {
  const [viewNewPassword, setNewPasswordView] = useState<boolean>(false);
  const [viewConfirmPassword, setConfirmPasswordView] =
    useState<boolean>(false);

  const isPassRequirement = (validationExp: RegExp | null) => {
    if (!validationExp) return false;
    if (!props || !props?.credentialData || !props?.credentialData?.password)
      return false;
    return validationExp.test(props?.credentialData?.password);
  };

  return (
    <>
      <label>
        <Text className="label-name">New Password</Text>

        <Box>
          <TextField.Root
            className={`${!props.credentialData?.isValidPassword && props.isSubmitted
                ? "input-invalid"
                : "input-valid"
              } placeholder:text-sm hide-ms-reveal`}
            size="3"
            autoComplete="new-password"
            defaultValue=""
            type={viewNewPassword ? "text" : "password"}
            placeholder=""
            maxLength={50}
            onChange={(e) =>
              props.handleCredentialData({
                ...props.credentialData,
                password: e.target.value,
                isValidConfirmPassword: isValidConfirmPassword(
                  e.target.value,
                  props.credentialData
                    ? props.credentialData.confirmPassword
                    : ""
                ),
                isValidPassword: isValidPassword(e.target.value, true),
              })
            }
            value={props.credentialData?.password}
          >
            <TextField.Slot>
              <PasswordViewIcon
                iconName={viewNewPassword ? "eyeOpen" : "eyeClose"}
                handleAction={() =>
                  viewNewPassword
                    ? setNewPasswordView(false)
                    : setNewPasswordView(true)
                }
              />
            </TextField.Slot>
          </TextField.Root>
          {!props.credentialData?.isValidPassword && props.isSubmitted && (
            <AlertMessage message=" Please enter valid password" />
          )}
        </Box>
      </label>

      <label>
        <Text className="label-name">Confirm Password</Text>

        <Box>
          <TextField.Root
            className={`${!props.credentialData?.isValidConfirmPassword &&
                props.credentialData?.isValidPassword &&
                props.isSubmitted
                ? "input-invalid"
                : "input-valid"
              } placeholder:text-sm hide-ms-reveal`}
            autoComplete="off"
            size="3"
            defaultValue=""
            maxLength={50}
            type={viewConfirmPassword ? "text" : "password"}
            placeholder=""
            onChange={(e) =>
              props.handleCredentialData({
                ...props.credentialData,
                confirmPassword: e.target.value,
                isValidConfirmPassword: isValidConfirmPassword(
                  props.credentialData?.password,
                  e.target.value
                ),
              })
            }
            value={props.credentialData?.confirmPassword}
          >
            <TextField.Slot>
              <PasswordViewIcon
                iconName={viewConfirmPassword ? "eyeOpen" : "eyeClose"}
                handleAction={() =>
                  viewConfirmPassword
                    ? setConfirmPasswordView(false)
                    : setConfirmPasswordView(true)
                }
              />
            </TextField.Slot>
          </TextField.Root>
          {!props.credentialData?.isValidConfirmPassword &&
            props.credentialData?.isValidPassword &&
            props.isSubmitted && (
              <AlertMessage
                message={
                  !props.credentialData?.confirmPassword ||
                    props.credentialData?.confirmPassword?.length === 0
                    ? "Please enter confirm password"
                    : "The entered password do not match"
                }
              />
            )}
        </Box>
      </label>
      <label>
        <Text
          className=" text-sm text-secondary-default flex"
          weight={"medium"}
        >
          Password Requirements:
        </Text>
        {PasswordRequirements.map((requirement, index: number) => (
          <Text
            key={"req" + index}
            as="div"
            size="1"
            mt="1"
            className={
              isPassRequirement(requirement.validation)
                ? "text-[#1cab50] font-medium"
                : "text-secondary-medium font-normal"
            }
          >
            {requirement.label}
          </Text>
        ))}
      </label>
    </>
  );
};

export default ResetPassword;
